import {
  ContentAlignment,
  contentAlignmentToFlex,
  DEFAULT_CONTENT_ALIGNMENT,
} from "@redotech/redo-content-builder-common/element-schema";
import {
  createContext,
  CSSProperties,
  memo,
  ReactNode,
  useContext,
} from "react";

/** The slot a sizing wrapper gives an element, whose box must fill it. */
export interface SizedBox {
  /** The slot is as tall as its content, so nothing can overflow it vertically. */
  hugsHeight: boolean;
}

/** Set inside a sizing wrapper; null where elements render as before sizing existed. */
export const SizedBoxContext = createContext<SizedBox | null>(null);

/**
 * Fills the sizing wrapper and positions the box's single child. Left stretches
 * instead, so the content's own alignment (like text alignment) still applies.
 */
export function sizedBoxStyles(
  alignment: ContentAlignment | undefined,
  { fillsWidth = false }: { fillsWidth?: boolean } = {},
): Pick<
  CSSProperties,
  | "display"
  | "flexDirection"
  | "flex"
  | "minHeight"
  | "justifyContent"
  | "alignItems"
> {
  const resolved = alignment ?? DEFAULT_CONTENT_ALIGNMENT;
  const { horizontal, vertical } = contentAlignmentToFlex(resolved);
  return {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: 0,
    justifyContent: vertical,
    alignItems:
      fillsWidth || resolved.horizontal === "left" ? "stretch" : horizontal,
  };
}

/**
 * The painted box of a leaf element. Outside a sizing wrapper it renders
 * exactly what the element rendered before sizing existed.
 */
export const ElementBox = memo(function ElementBox({
  alignment,
  fillsWidth = false,
  alignsText = false,
  unclippedWhileHugging = false,
  style,
  bare = false,
  id,
  children,
}: {
  alignment: ContentAlignment | undefined;
  /** Span the box's width, leaving horizontal alignment to the content (e.g. text). */
  fillsWidth?: boolean;
  /** Span the box's width and justify the text by the horizontal alignment. */
  alignsText?: boolean;
  /** Skip clipping while the height hugs, so painting past the box (like a focus ring) shows. */
  unclippedWhileHugging?: boolean;
  style?: CSSProperties;
  /** Render no box of its own outside a sizing wrapper. */
  bare?: boolean;
  id?: string;
  children: ReactNode;
}) {
  const sizedBox = useContext(SizedBoxContext);
  if (!sizedBox) {
    return bare ? (
      <>{children}</>
    ) : (
      // design-allow: merchant-authored element styles have no tokens
      <div id={id} style={style}>
        {children}
      </div>
    );
  }
  return (
    // design-allow: merchant-authored element styles and alignment have no tokens
    <div
      id={id}
      // design-allow: clips content past its size, like a non-growing container
      style={{
        ...style,
        ...sizedBoxStyles(alignment, { fillsWidth: fillsWidth || alignsText }),
        overflow:
          unclippedWhileHugging && sizedBox.hugsHeight ? "visible" : "hidden",
        ...(alignsText && alignment && { textAlign: alignment.horizontal }),
      }}
    >
      {children}
    </div>
  );
});
